import React, { memo } from 'react'
import classNames from 'classnames'
import { Tooltip, ITooltipProps } from 'transfix-ui/components/Tooltip'
import uuid from 'uuid/v4'
import { UserIcon } from 'transfix-icons'
import getInitials from 'utils/initials'
import 'scss/5_components/_avatar.scss'

const COLOR_COUNT = 10

enum AvatarSize {
  xsmall = 'xsmall',
  small = 'small',
  medium = 'medium',
  input = 'input',
}

export const getColorIndex = (name?: string) =>
  !name
    ? 'default'
    : name
        .split('')
        .map(letter => letter.charCodeAt(0))
        .reduce((acc, letterCode) => acc + letterCode, 0) % COLOR_COUNT

export interface IAvatarProps {
  id?: string
  dataTest?: string
  name?: string
  defaultVal?: string
  tooltip?: JSX.Element
  tooltipName?: string
  details?: string
  size?: keyof typeof AvatarSize
  tooltipProps?: Partial<ITooltipProps>
  hideTooltip?: boolean
  className?: string
  colorClassName?: string
  tooltipClassName?: string
  /**
   * @default false
   */
  hideShadow?: boolean
}

export const Avatar = memo<IAvatarProps>(
  ({
    id = uuid(),
    dataTest = '',
    name,
    defaultVal,
    tooltip,
    tooltipName,
    details,
    size = 'small',
    tooltipProps,
    hideTooltip,
    className,
    colorClassName,
    tooltipClassName,
    hideShadow = false,
  }) => {
    const avatarName = name?.toLowerCase() !== 'unassigned' ? name : undefined
    const avatarColor = colorClassName
      ? `avatar-color-${colorClassName}`
      : `avatar-color-${getColorIndex(avatarName)}`

    const classes = classNames('avatar', size, className, avatarColor, {
      'avatar-type-unassigned': avatarName === undefined,
      'no-shadow': hideShadow,
    })

    const avatar = (
      <span className={classes} data-test={dataTest}>
        {avatarName ? (
          <span>
            {size === AvatarSize.xsmall || size === AvatarSize.input
              ? avatarName[0]
              : getInitials(avatarName)}
          </span>
        ) : (
          <UserIcon />
        )}
      </span>
    )

    const { delayShow, effect, overridePosition, type } = tooltipProps || {}
    const tooltipClasses = classNames(tooltipClassName)

    return hideTooltip ? (
      avatar
    ) : (
      <Tooltip
        id={`${id}-tooltip`}
        position='bottom'
        type={type}
        effect={effect}
        delayShow={delayShow}
        overridePosition={overridePosition}
        trigger={avatar}>
        <div className={tooltipClasses}>
          {tooltip ? (
            <>{tooltip}</>
          ) : (
            <>
              <strong>{tooltipName ?? name ?? defaultVal ?? ''}</strong>
              {details && <div>{details}</div>}
            </>
          )}
        </div>
      </Tooltip>
    )
  }
)

export default Avatar
